// Per-tool page SEO injector — matches the videoSeo.js pattern.
// Injects rich Open Graph + Twitter + JSON-LD CollectionPage so Google can
// rank these pages for long-tail queries like "Suno music videos".

const MANAGED = 'data-aivue-dynamic-seo';
const SITE = 'https://audivue.io';
const FALLBACK_OG = `${SITE}/og-image.jpg`;

/**
 * OG-safe thumbnail resolver used by SEO metadata (og:image, twitter:image,
 * JSON-LD `image`). Mirrors the priority order used by resolveThumbnailUrl()
 * in hlsPlayer.js, but produces ABSOLUTE audivue.io URLs and, crucially,
 * routes Bunny thumbnails through our /api/og-thumb proxy so Facebook /
 * LinkedIn scrapers don't hit Bunny's hotlink-protected 403.
 *
 * Priority: custom_thumbnail_url → OG proxy (Bunny) → thumbnail_url →
 *           /api/files/{thumbnail_path} → fallback OG image.
 */
function seoThumbnailFor(v) {
  if (!v) return FALLBACK_OG;
  if (v.custom_thumbnail_url) return v.custom_thumbnail_url;
  const bunnyLike = v.bunny_video_guid
    || (typeof v.thumbnail_url === 'string' && v.thumbnail_url.includes('.b-cdn.net'));
  if (bunnyLike && v.id) return `${SITE}/api/og-thumb/${v.id}.jpg`;
  if (v.thumbnail_url && v.thumbnail_url.startsWith('http')) return v.thumbnail_url;
  if (v.thumbnail_path) return `${SITE}/api/files/${v.thumbnail_path}`;
  return FALLBACK_OG;
}

function setMeta({ key, name, property, content }) {
  if (!content) return;
  const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    if (name) el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    el.setAttribute(MANAGED, key);
    document.head.appendChild(el);
  } else if (!el.hasAttribute(MANAGED)) {
    el.setAttribute('data-aivue-original', el.getAttribute('content') || '');
    el.setAttribute(MANAGED, key);
  }
  el.setAttribute('content', content);
}

function setLink({ key, rel, href }) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED, key);
    document.head.appendChild(el);
  } else if (!el.hasAttribute(MANAGED)) {
    el.setAttribute('data-aivue-original', el.getAttribute('href') || '');
    el.setAttribute(MANAGED, key);
  }
  el.setAttribute('href', href);
}

function injectLd(key, payload) {
  document.querySelectorAll(`script[${MANAGED}="${key}"]`).forEach((n) => n.remove());
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute(MANAGED, key);
  s.textContent = JSON.stringify(payload);
  document.head.appendChild(s);
}

export function applyToolSeo({ tool, totalVideos, topVideos = [], canonicalPath }) {
  if (!tool || !tool.name) return () => {};

  const toolName = tool.name;
  const title = `${toolName} Music Videos — Made with ${toolName} on AUDIVUE`;
  const desc = `Discover the best AI music videos made with ${toolName} on AUDIVUE — ${totalVideos || 'a growing library of'} videos by AI artists. Stream, like, and explore the full ${toolName} catalog free.`;
  const url = `${SITE}${canonicalPath || `/tools/${tool.key}`}`;
  const ogImage = seoThumbnailFor(topVideos[0]);

  document.title = title;
  setMeta({ key: 'tool-title', name: 'title', content: title });
  setMeta({ key: 'tool-desc', name: 'description', content: desc });
  setMeta({
    key: 'tool-keywords',
    name: 'keywords',
    content: [
      `${toolName} music videos`,
      `${toolName} AI`,
      `made with ${toolName}`,
      `${toolName} videos`,
      `AI music videos`,
      `AI generated music`,
      'AUDIVUE',
    ].join(', '),
  });

  setMeta({ key: 'og-type', property: 'og:type', content: 'website' });
  setMeta({ key: 'og-title', property: 'og:title', content: title });
  setMeta({ key: 'og-desc', property: 'og:description', content: desc });
  setMeta({ key: 'og-url', property: 'og:url', content: url });
  setMeta({ key: 'og-image', property: 'og:image', content: ogImage });

  setMeta({ key: 'tw-card', name: 'twitter:card', content: 'summary_large_image' });
  setMeta({ key: 'tw-title', name: 'twitter:title', content: title });
  setMeta({ key: 'tw-desc', name: 'twitter:description', content: desc });
  setMeta({ key: 'tw-image', name: 'twitter:image', content: ogImage });

  setLink({ key: 'canonical', rel: 'canonical', href: url });

  injectLd('tool-collection', {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description: desc,
    url,
    isPartOf: { '@type': 'WebSite', name: 'AUDIVUE', url: 'https://audivue.io' },
    about: {
      '@type': 'SoftwareApplication',
      name: toolName,
      applicationCategory: 'MultimediaApplication',
      url: tool.url || undefined,
      description: tool.description || undefined,
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: totalVideos || topVideos.length,
      itemListElement: topVideos.slice(0, 10).map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://audivue.io/video/${v.id}`,
        name: v.title,
      })),
    },
  });

  injectLd('tool-breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://audivue.io/' },
      { '@type': 'ListItem', position: 2, name: 'AI Tools', item: 'https://audivue.io/tools' },
      { '@type': 'ListItem', position: 3, name: toolName, item: url },
    ],
  });

  return () => {
    document.querySelectorAll(`[${MANAGED}]`).forEach((el) => {
      const original = el.getAttribute('data-aivue-original');
      if (original !== null) {
        if (el.tagName === 'LINK') el.setAttribute('href', original);
        else el.setAttribute('content', original);
        el.removeAttribute('data-aivue-original');
        el.removeAttribute(MANAGED);
      } else {
        el.remove();
      }
    });
    document.title = 'AUDIVUE — The Home of AI Music Videos.';
  };
}
